import { AuthEnums } from "models/enums";
import { ServiceConstants } from "utils/constants";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { KeyValue } from "models/utilTypes/keyValue";
import { DimensionMap, DimensionData } from "models/propTypes/dimensionPropTypes";
import { SelectionItem } from "models/utilTypes/selectionTypes";
import { LocalizationHelperInstance, ApiClientInstance, ServiceHelper, MathHelper } from "utils/helpers";

/**
 * @deprecated This service is deprecated. Use useDimensionService hook instead.
 *
 * Migration guide:
 * 1. Replace DimensionService calls with useDimensionService hook
 * 2. Move pure utility functions to appropriate helper files
 * 3. Use Redux hooks for state management
 *
 * @see useDimensionService - The replacement hook
 * @see utils/helpers/ - Directory containing utility functions
 * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
 */
const DimensionService = {
  getAllDimensions: () => {
    return ServiceHelper.getAllPages((page) =>
      ApiClientInstance(AuthEnums.AuthType.Company).DimensionClient.Get({
        ...ServiceConstants.DefaultFilter,
        page: page,
      })
    );
  },

  getAllDimensionsByType: (typeId: string) => {
    return ServiceHelper.getAllPages((page) =>
      ApiClientInstance(AuthEnums.AuthType.Company).DimensionClient.Get({
        ...ServiceConstants.DefaultFilter,
        page: page,
        typeId: typeId,
      })
    );
  },

  getAllDimensionsByTypes: async (typeIds: string[]) => {
    const dimensionsResult = await Promise.all(
      typeIds.map((typeId) => DimensionService.getAllDimensionsByType(typeId))
    );
    return dimensionsResult.flatMap((d) => d);
  },

  getInvoiceDimensions: () => {
    return ApiClientInstance(AuthEnums.AuthType.Company)
      .DimensionClient.GetDimensionsForInvoice()
      .then((res) => res.data);
  },

  getInvoiceDimensionTypes: () => {
    return ApiClientInstance(AuthEnums.AuthType.Company)
      .DimensionTypesClient.GetDimensionTypesForInvoice()
      .then((res) => res.data);
  },

  getIncomingInvoiceDimensions: () => {
    return ApiClientInstance(AuthEnums.AuthType.Company)
      .DimensionClient.GetDimensionsForIncomingInvoice()
      .then((res) => res.data);
  },

  getIncomingInvoiceDimensionTypes: () => {
    return ApiClientInstance(AuthEnums.AuthType.Company)
      .DimensionTypesClient.GetDimensionTypesForIncomingInvoice()
      .then((res) => res.data);
  },

  getReceiptDimensions: () => {
    return ApiClientInstance(AuthEnums.AuthType.Company)
      .DimensionClient.GetDimensionsForReceipts()
      .then((res) => res.data);
  },

  getReceiptDimensionTypes: () => {
    return ApiClientInstance(AuthEnums.AuthType.Company)
      .DimensionTypesClient.GetDimensionTypesForReceipts()
      .then((res) => res.data);
  },

  getIncomingInvoiceDimensionSuggestions: () => {
    return ApiClientInstance(AuthEnums.AuthType.Company)
      .IncomingInvoiceClient.GetDimensionSuggestions({})
      .then((res) => res.data);
  },

  getReceiptDimensionSuggestions: () => {
    return ApiClientInstance(AuthEnums.AuthType.Company)
      .ReceiptClient.GetDimensionSuggestions()
      .then((res) => res.data);
  },

  getInvoiceDimensionSuggestions: () => {
    return ApiClientInstance(AuthEnums.AuthType.Company)
      .InvoiceClient.GetDimensionSuggestions()
      .then((res) => res.data);
  },

  getGroupedDimensionSuggestions: (
    suggestions: string[],
    types: WintSuperkollAPIModel.DimensionType[],
    dimensions: WintSuperkollAPIModel.Dimension[]
  ): DimensionMap => {
    return types.map((type) => {
      return new KeyValue<WintSuperkollAPIModel.DimensionType, WintSuperkollAPIModel.Dimension[]>(
        type,
        dimensions.filter((item) => item.typeId === type.id && item.id && suggestions.includes(item.id))
      );
    });
  },

  getGroupedCompanyDimensions: (
    types: WintSuperkollAPIModel.DimensionType[],
    dimensions: WintSuperkollAPIModel.Dimension[]
  ): DimensionMap => {
    return types.map((type) => {
      const dimensionsOfType = Array.from(
        new Set(dimensions.filter((item) => item.typeId === type.id).map((d) => d.id))
      )
        .map((d) => dimensions.find((r) => r.id === d))
        .filter((t) => t !== undefined) as WintSuperkollAPIModel.Dimension[];
      return new KeyValue<WintSuperkollAPIModel.DimensionType, WintSuperkollAPIModel.Dimension[]>(
        type,
        dimensionsOfType
      );
    });
  },

  convertDimensionToSelectionItem: (
    dimension: WintSuperkollAPIModel.Dimension,
    amount: number,
    currency?: string,
    dimensionData?: WintSuperkollAPIModel.DimensionData,
    alwaysDisplayAmounts?: boolean,
    currentData?: DimensionData
  ): SelectionItem<DimensionData> => {
    return {
      id: dimension.id ?? "",
      value: dimension.name ?? "",
      data: dimensionData
        ? {
            amount: dimensionData && amount ? dimensionData.amount : 0,
            percentage:
              dimensionData && amount
                ? MathHelper.toFixedNoRound((dimensionData.amount / amount) * 100, 2)
                : !amount
                  ? (currentData?.percentage ?? 100)
                  : 0,
            fraction:
              dimensionData && amount
                ? [dimensionData.amount, amount]
                : !amount
                  ? (currentData?.fraction ?? [1, 1])
                  : [0, 1],
            dimensionId: dimension.id ?? "",
          }
        : ({} as DimensionData),
      selectedSubText:
        alwaysDisplayAmounts || (dimensionData && dimensionData.amount < amount)
          ? LocalizationHelperInstance.getFormattedCurrency(dimensionData?.amount ?? 0, currency, 2, 2)
          : "",
      description: dimension.shortName,
    };
  },
};

export default DimensionService;
